import React from "react";
import { graphql, PageProps } from 'gatsby';

import {styled, Box} from '@mui/material';

import Hero from '../components/Hero';
import Stats from '../components/Stats';
import Text from '../components/Text';
import Footer from '../components/Footer';

import VerticalSpaceContainer from "../components/atoms/VerticalSpaceContainer";
import AppBar from '../components/atoms/AppBar';
import CursorCircle from "../components/atoms/CursorCircle";
import RandomImages from "../components/RandomImages";
import Masthead from "../components/Masthead";
import Contact from "../components/ContactForm";
import RichText from "../components/RichText";
import CaptionedImage from "../components/CaptionedImage"

import type { 
    ComposablePage      as ComposablePage, 
    Masthead            as MastheadProps,
    Footer              as FooterProps,
    // Links               as LinksProps,
    AppBar              as AppBarProps
} from '../types/app';

const componentMap = {
    ContentfulHero              : Hero,
    ContentfulStats             : Stats,
    ContentfulText              : Text,
    ContentfulRandomImages      : RandomImages,
    ContentfulMasthead          : Masthead,
    ContentfulContact           : Contact,
    ContentfulRichText          : RichText,
    ContentfulCaptionedImage    : CaptionedImage,
};

const Container = styled(VerticalSpaceContainer)({
    minHeight : 250
})

export const ComposablePageTemplate : React.FC<PageProps> = ({ data }) => {
    
    const page = (data as any).contentfulPage as ComposablePage;

    React.useEffect(() => {
        
        if (typeof window === 'undefined' || !window.document) {
            return;
        }

        const eventName = "stackbitObjectsChanged";
        const handleContentChange = async (event: Event) => {
            event.preventDefault();
            await fetch("/__refresh", { method: "POST" });
        };
        window.addEventListener(eventName, handleContentChange);
        return () => {
            window.removeEventListener(eventName, handleContentChange);
        };

    }, []);

    // ...
    return (
        <>
            <AppBar {...(page.appBar as AppBarProps)} />
            <Masthead {...(page.masthead as MastheadProps)} />
            <Container>
                { 
                    (page.sections || []).map((section, idx) => {
                        const Component = componentMap[section.__typename] as any;

                        // Contentful Stats
                        if(section.__typename === 'ContentfulStats'){
                            return (
                                null
                            );
                        }

                        // Contentful Hero
                        if(section.__typename === 'ContentfulHero'){
                            return (
                                <Box key={idx} id={section?.anchor}>
                                    <Component {...section} />
                                </Box>
                            )
                        }

                        // Contentful Random Images
                        if(section.__typename === 'ContentfulRandomImages'){
                            return (
                                <Box key={idx} sx={{my:'0px!important'}}>
                                    <Component {...section} />
                                </Box>
                            )
                        }

                        // Fallback
                        return (
                            <Box key={idx}>
                                <Component {...section} />
                            </Box>
                        )
                    })
                }
            </Container>
            <Footer {...(page.footer as FooterProps)}/>
            <CursorCircle />
        </>
    );
}

export default ComposablePageTemplate;

export const query = graphql`
    query ComposablePageQuery($slug: String!) {
        contentfulPage(slug: { eq: $slug }) {
            __typename
            contentful_id
            slug
            masthead {
                __typename
                contentful_id
                heading
                tagline,
                imageSet {
                    __typename
                    contentful_id,
                    images {
                        ... on ContentfulAsset {
                            __typename
                            contentful_id
                            title
                            gatsbyImageData(
                                layout: FULL_WIDTH
                                width: 600
                                formats: [PNG]
                                placeholder: BLURRED
                                backgroundColor: "transparent"
                            )
                        }  
                    }
                }
            }
            appBar {
                __typename
                contentful_id
                logo {
                    gatsbyImageData(
                        layout: FULL_WIDTH
                        width: 100
                        formats: [PNG]
                        backgroundColor: "transparent"
                    )
                    title
                }
                links {
                    links {
                        ... on ContentfulPage {
                            __typename
                            contentful_id
                            slug 
                        }
                    }  
                }
            }
            sections {
                ... on ContentfulHero {
                    __typename
                    contentful_id
                    heading
                    body {
                        body
                    }
                    theme
                    anchor
                    button {
                        contentful_id
                        url
                        theme
                        label
                    }
                    image {
                        gatsbyImageData(
                            layout: FULL_WIDTH
                            width: 800
                            formats: [PNG]
                            backgroundColor: "transparent"
                        )
                        title
                    }
                    imageFullWidth
                }
                ... on ContentfulCaptionedImage {
                    __typename
                    contentful_id
                    maxWidth
                    caption
                    image {
                        gatsbyImageData(
                            layout: FULL_WIDTH
                            width: 800
                            formats: [PNG]
                            backgroundColor: "transparent"
                        )
                        title
                    }
                }
                ... on ContentfulStats {
                    __typename
                    contentful_id
                    heading
                    body {
                        body
                    }
                    theme
                    stats {
                        contentful_id
                        value
                        label
                    }
                }
                ... on ContentfulText {
                    __typename
                    contentful_id
                    heading
                    body {
                        body
                    }
                }
                ... on ContentfulRichText {
                    __typename
                    contentful_id
                    body {
                        raw
                        references {
                            ... on ContentfulAsset {
                                __typename
                                contentful_id
                                title
                                gatsbyImageData(
                                    layout: FULL_WIDTH
                                    width: 800
                                    formats: [PNG]
                                    backgroundColor: "transparent"
                                )
                            }   
                            ... on ContentfulPage {
                                __typename
                                contentful_id
                                slug
                            }
                        }
                    }
                }
                ... on ContentfulRandomImages {
                    __typename
                    contentful_id
                    quantity
                    imageSet {
                        __typename
                        contentful_id,
                        images {
                            ... on ContentfulAsset {
                                __typename
                                contentful_id
                                title
                                gatsbyImageData(
                                    layout: FULL_WIDTH
                                    width: 600
                                    formats: [PNG]
                                    placeholder: BLURRED
                                    backgroundColor: "transparent"
                                )
                            }  
                        }
                    }
                }
                ... on ContentfulMasthead {
                    __typename
                    contentful_id
                    heading
                    tagline,
                    imageSet {
                        __typename
                        contentful_id,
                        images {
                            ... on ContentfulAsset {
                                __typename
                                contentful_id
                                title
                                gatsbyImageData(
                                    layout: FULL_WIDTH
                                    width: 600
                                    formats: [PNG]
                                    placeholder: BLURRED
                                    backgroundColor: "transparent"
                                )
                            }  
                        }
                    }
                }
                ... on ContentfulContact {
                    __typename
                    contentful_id
                    heading
                    body {
                        body
                    }
                }
            }
            footer {
                __typename
                contentful_id
                internalName
                sections {
                    ... on ContentfulFooterText {
                        __typename
                        contentful_id
                        internalName
                        heading
                        body {
                            body
                        }
                    }
                    ... on ContentfulFooterLinks {
                        __typename
                        contentful_id
                        internalName
                        heading
                        links {
                            __typename
                            contentful_id
                            links {
                                ... on ContentfulPage {
                                    __typename
                                    contentful_id
                                    slug 
                                }
                            }
                        }
                    }
                    ... on ContentfulFooterSocial {
                        __typename
                        contentful_id
                        internalName
                        heading
                    }
                }
            }
        }
    }
`;

/*
  
links {
    __typename
    contentful_id
    links {
        ... on ContentfulPage {
            __typename
            contentful_id
            slug 
        }
    }   
}
        
*/